import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Facebook, Instagram, Sun, Moon, ChevronDown, CheckCircle2, MessageCircle, Calendar, HelpCircle, Menu, X } from 'lucide-react';
import './App.css';

function App() {
  const [config, setConfig] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [assessmentStep, setAssessmentStep] = useState(0);
  const [assessmentAnswers, setAssessmentAnswers] = useState([]);
  const [showAssessmentResult, setShowAssessmentResult] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetch('/config.json')
      .then(res => res.json())
      .then(data => setConfig(data))
      .catch(err => console.error('Error loading config:', err));
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const assessmentQuestions = [
    { question: "Дали вашето дете има тешкотии со изговорот на одредени гласови?", id: 1 },
    { question: "Дали развојот на говорот на вашето дете е задоцнет во однос на врсниците?", id: 2 },
    { question: "Дали вашето дете повторува гласови, зборови или дел од зборовите ?", id: 3 },
    { question: "Дали вашето дете има тешкотии со читањето или пишувањето?", id: 4 },
    { question: "Дали вашето дете има тешкотии со социјалната комуникација?", id: 5 }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  const handleAssessmentAnswer = (answer) => {
    const newAnswers = [...assessmentAnswers, answer];
    setAssessmentAnswers(newAnswers);
    
    if (assessmentStep < assessmentQuestions.length - 1) {
      setAssessmentStep(assessmentStep + 1);
    } else {
      setShowAssessmentResult(true);
    }
  };

  const resetAssessment = () => {
    setAssessmentStep(0);
    setAssessmentAnswers([]);
    setShowAssessmentResult(false);
  };

  const yesAnswersCount = assessmentAnswers.filter(a => a === 'yes').length;

  if (!config) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-cream">
        <div className="text-orange-500 text-2xl font-nunito">Се вчитува...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream dark:bg-gray-900 transition-colors duration-300 font-dmsans">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-sm z-40 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-28">
            <div className="flex items-center space-x-4">
              <img 
                src={config.logo}
                alt="Логосвет" 
                className="h-12 w-auto flex-shrink-0"
              />
              <div className="flex flex-col justify-center h-24">
                <h1 className="text-xl md:text-2xl font-nunito font-bold text-gray-800 dark:text-white leading-tight">{config.cabinetName}</h1>
                <p className="text-sm md:text-base text-gray-600 dark:text-gray-300">{config.cabinetNameSubtitle}</p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 dark:text-gray-300 hover:text-orange-500 transition-colors">Дома</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 dark:text-gray-300 hover:text-orange-500 transition-colors">За нас</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 dark:text-gray-300 hover:text-orange-500 transition-colors">Услуги</button>
              <button onClick={() => scrollToSection('schedule')} className="text-gray-700 dark:text-gray-300 hover:text-orange-500 transition-colors">Работно време</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 dark:text-gray-300 hover:text-orange-500 transition-colors">Контакт</button>
              <button 
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                data-testid="theme-toggle-button"
              >
                {darkMode ? <Sun className="h-5 w-5 text-yellow-400" /> : <Moon className="h-5 w-5 text-gray-600" />}
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center space-x-2">
              <button 
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                data-testid="mobile-theme-toggle"
              >
                {darkMode ? <Sun className="h-5 w-5 text-yellow-400" /> : <Moon className="h-5 w-5 text-gray-600" />}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="h-6 w-6 text-gray-800 dark:text-white" /> : <Menu className="h-6 w-6 text-gray-800 dark:text-white" />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200 dark:border-gray-700" data-testid="mobile-menu">
              <div className="flex flex-col space-y-3">
                <button 
                  onClick={() => { scrollToSection('home'); setMobileMenuOpen(false); }} 
                  className="text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Дома
                </button>
                <button 
                  onClick={() => { scrollToSection('about'); setMobileMenuOpen(false); }} 
                  className="text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  За нас
                </button>
                <button 
                  onClick={() => { scrollToSection('services'); setMobileMenuOpen(false); }} 
                  className="text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Услуги
                </button>
                <button 
                  onClick={() => { scrollToSection('schedule'); setMobileMenuOpen(false); }} 
                  className="text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Работно време
                </button>
                <button 
                  onClick={() => { scrollToSection('contact'); setMobileMenuOpen(false); }} 
                  className="text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Контакт
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-4 overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <h2 className="text-5xl md:text-6xl lg:text-5xl font-nunito font-bold text-gray-900 dark:text-white tracking-tight leading-tight">
                Секое дете заслужува да биде слушнато
              </h2>
              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                Професионална логопедска поддршка за вашето дете во топла и пријателска средина. Индивидуален пристап за секое дете.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href={`tel:${config.phone}`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
                  data-testid="hero-cta-button"
                >
                  <Phone className="h-5 w-5" />
                  Јавете се
                </a>
                <button 
                  onClick={() => scrollToSection('assessment')}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 border-2 border-orange-100 dark:border-orange-500 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-gray-700 rounded-full font-semibold transition-colors"
                  data-testid="assessment-cta-button"
                >
                  Самопроцена
                </button>
              </div>
              <div className="flex items-center gap-8 pt-4">
                <div>
                  <div className="text-3xl font-nunito font-bold text-orange-500">4+</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Години искуство</div>
                </div>
                <div>
                  <div className="text-3xl font-nunito font-bold text-orange-500">50+</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Задоволни семејства</div>
                </div>
                <div>
                  <div className="text-3xl font-nunito font-bold text-orange-500">100%</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Индивидуален пристап</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-[3rem] overflow-hidden shadow-2xl">
                <img 
                  src="/room.jpg"
                  alt="Логопедски кабинет"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-orange-500 text-white rounded-3xl p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-8 w-8" />
                  <div>
                    <div className="font-bold text-lg">Простор прилагоден за деца</div>
                    {/* <div className="text-sm opacity-90">простор</div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 bg-white dark:bg-gray-800 transition-colors duration-300" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-nunito font-bold text-gray-900 dark:text-white mb-4">За логопедот</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">Стручност, искуство и љубов кон децата</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
  {/* LEFT */}
  <div className="space-y-6">
    <h3 className="text-2xl font-nunito font-bold text-gray-900 dark:text-white">
      Специјалист клинички логопед Стефанија Брзачка
    </h3>
        
    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
      Со индивидуален пристап прилагоден за секое дете, логопедот
       го поттикнува развојот на јасен говор, правилна артикулација, збогатување на речникот и сигурна комуникација.
    </p>

    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="h-6 w-6 text-orange-500 mt-1" />
        <div>
          <div className="font-semibold text-gray-900 dark:text-white">
            Образование
          </div>
          <div className="text-gray-600 dark:text-gray-400">
            Специјалист клинички логопед
          </div>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <CheckCircle2 className="h-6 w-6 text-orange-500 mt-1" />
        <div>
          <div className="font-semibold text-gray-900 dark:text-white">
            Фокус на работа
          </div>
          <div className="text-gray-600 dark:text-gray-400">
            Работа со деца (2–10 години)
          </div>
        </div>
      </div>

      <div className="flex items-start gap-3">
        <CheckCircle2 className="h-6 w-6 text-orange-500 mt-1" />
        <div>
          <div className="font-semibold text-gray-900 dark:text-white">
            Пристап
          </div>
          <div className="text-gray-600 dark:text-gray-400">
            Индивидуален и детски ориентиран
          </div>
        </div>
      </div>
    </div>
  </div>

  {/* RIGHT */}
  <div className="w-full flex justify-center md:justify-end">
    <div className="w-64 md:w-72 lg:w-[380px]">
      <img
        src="/therapist.jpg"
        alt="Логопедски услуги"
        className="rounded-3xl shadow-xl w-full h-auto"
      />
    </div>
  </div>
</div>

        </div>
      </section>

      {/* Services Section */}
<section id="services" className="py-20 px-4" data-testid="services-section">
  <div className="max-w-7xl mx-auto">
    <div className="text-center mb-16">
      <h2 className="text-4xl md:text-5xl font-nunito font-bold text-gray-900 dark:text-white mb-4">
        Наши услуги
      </h2>
      <p className="text-xl text-gray-600 dark:text-gray-300">
        Логопедски третмани за деца
      </p>
    </div>

    {/* IMPORTANT: items-stretch so cards in a row share height */}
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
      {config.services.map((service, index) => (
        <div
          key={service.id}
          className="
            bg-white dark:bg-gray-800
            rounded-3xl shadow-lg hover:shadow-xl
            border border-orange-50 dark:border-gray-700
            hover:border-orange-200 dark:hover:border-orange-500
            transition-all duration-300 hover:-translate-y-1
            p-6
            h-full flex flex-col
          "
          style={{ animationDelay: `${index * 100}ms` }}
          data-testid={`service-card-${service.id}`}
        >
          {/* Header/icon */}
          <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center mb-4">
            <MessageCircle className="h-6 w-6 text-orange-500" />
          </div>

          {/* Content block grows to push footer down */}
          <div className="flex-1">
            <h3 className="text-xl font-nunito font-bold text-gray-900 dark:text-white mb-3">
               {service.title}
            </h3>

            {/* Keep it natural; clamp optional */}
            {/* <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              {service.description}
            </p> */}

            {/* “2 new lines” space (consistent) */}
            <div className="h-3" aria-hidden="true" />
          </div>

          {/* Footer pinned to bottom */}
          <div className="mt-auto pt-4 border-t border-orange-50 dark:border-gray-700 flex items-center justify-between text-sm text-gray-500">
            <span className="inline-flex items-center gap-2">
              {/* <span aria-hidden>🎂</span> {service.ageRange} */}
            </span>
            <span className="inline-flex items-center gap-2">
              <span aria-hidden>⏱️</span> {service.duration}
            </span>
          </div>
        </div>
      ))}
    </div>
  </div>
</section>


      {/* Schedule Section */}
      <section id="schedule" className="py-20 px-4 bg-white dark:bg-gray-800 transition-colors duration-300" data-testid="schedule-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-nunito font-bold text-gray-900 dark:text-white mb-4">Работно време</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">Закажете термин телефонски</p>
          </div>
          
          <div className="bg-gradient-to-br from-orange-50 to-cream dark:from-gray-700 dark:to-gray-800 rounded-3xl shadow-xl p-8">
            <div className="space-y-4">
              {Object.entries(config.workingHours).map(([day, hours]) => (
                <div key={day} className="flex justify-between items-center py-3 border-b border-orange-100 dark:border-gray-600 last:border-0">
                  <span className="font-semibold text-gray-900 dark:text-white capitalize">
                    {day === 'monday' && 'Понеделник'}
                    {day === 'tuesday' && 'Вторник'}
                    {day === 'wednesday' && 'Среда'}
                    {day === 'thursday' && 'Четврток'}
                    {day === 'friday' && 'Петок'}
                    {day === 'saturday' && 'Сабота'}
                    {day === 'sunday' && 'Недела'}
                  </span>
                  <span className="text-gray-700 dark:text-gray-300 font-medium">{hours}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 p-4 bg-white dark:bg-gray-900 rounded-2xl">
              <p className="text-center text-gray-700 dark:text-gray-300">
                <Calendar className="inline h-5 w-5 mr-2 text-orange-500" />
                За закажување термин, ве молиме јавете се телефонски на <a href={`tel:${config.phone}`} className="text-orange-500 font-bold hover:underline">{config.phone}</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Self-Assessment Section */}
      <section id="assessment" className="py-20 px-4" data-testid="assessment-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-nunito font-bold text-gray-900 dark:text-white mb-4">Самопроцена</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">Доколку имате потврден одговор на некое од следните прашања,
               препорачуваме консултација со логопед.</p>
          </div>
          
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-gray-700 dark:to-gray-800 rounded-3xl shadow-xl p-8 md:p-12">
            {!showAssessmentResult ? (
              <div className="space-y-6">
                <div className="flex justify-between items-center mb-8">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Прашање    {assessmentStep + 1} од {assessmentQuestions.length}</span>
                  <div className="flex gap-1.5">
                    {assessmentQuestions.map((_, index) => (
                      <div 
                        key={index}
                        className={`h-2 w-8 rounded-full ${index <= assessmentStep ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                      />
                    ))}
                  </div>
                </div>
                
                <div className="mb-8">
                  <div className="flex items-start gap-3 mb-6">
                    <HelpCircle className="h-6 w-6 text-orange-500 flex-shrink-0 mt-1" />
                    <h3 className="text-xl md:text-2xl font-nunito font-bold text-gray-900 dark:text-white">
                      {assessmentQuestions[assessmentStep].question}
                    </h3>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => handleAssessmentAnswer('yes')}
                    className="flex-1 py-4 px-6 bg-orange-500 hover:bg-orange-600 text-white rounded-2xl font-bold text-lg transition-colors"
                    data-testid="assessment-yes-button"
                  >
                    Да
                  </button>
                  <button
                    onClick={() => handleAssessmentAnswer('no')}
                    className="flex-1 py-4 px-6 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-white border-2 border-gray-200 dark:border-gray-600 rounded-2xl font-bold text-lg transition-colors"
                    data-testid="assessment-no-button"
                  >
                    Не
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-6" data-testid="assessment-result">
                <div className="w-20 h-20 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="h-10 w-10 text-orange-500" />
                </div>
                
                <h3 className="text-2xl md:text-3xl font-nunito font-bold text-gray-900 dark:text-white">
                  Ако имате дополнителни грижи, слободно контактирајте нѐ.
                </h3>
                
                {/* <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                  {yesAnswersCount >= 2 
                    ? 'Врз основа на вашите одговори, препорачуваме да закажете термин за професионална проценка. Јавете се за повеќе информации.'
                    : 'Врз основа на вашите одговори, изгледа дека вашето дете се развива нормално. Сепак, ако имате дополнителни грижи, слободно контактирајте нѐ.'}
                </p> */}
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <a
                    href={`tel:${config.phone}`}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all"
                    data-testid="assessment-contact-button"
                  >
                    <Phone className="h-5 w-5" />
                    Јавете се
                  </a>
                  <button
                    onClick={resetAssessment}
                    className="px-6 py-3 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-white border-2 border-gray-200 dark:border-gray-600 rounded-full font-semibold transition-colors"
                    data-testid="assessment-reset-button"
                  >
                    Повтори тест
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <section id="testimonials" className="py-20 px-4" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-nunito font-bold text-gray-900 dark:text-white mb-4">Што кажуваат родителите</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">Искуства од задоволни семејства</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {config.testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.id}
                className="bg-white dark:bg-gray-800 rounded-3xl shadow-lg p-8 hover:-translate-y-1 transition-transform"
                style={{ animationDelay: `${index * 150}ms` }}
                data-testid={`testimonial-${testimonial.id}`}
              >
                <div className="mb-4">
                  <div className="text-orange-500 text-4xl">"</div>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed font-caveat text-xl">
                  {testimonial.text}
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                    <span className="text-orange-500 font-bold">{testimonial.author.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white text-sm">{testimonial.author}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-white dark:bg-gray-800 transition-colors duration-300" data-testid="contact-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-nunito font-bold text-gray-900 dark:text-white mb-4">Контактирајте нѐ</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">Слободно обратете се за закажување термин</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="h-6 w-6 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">Телефон</h3>
                  <a href={`tel:${config.phone}`} className="text-orange-500 text-lg hover:underline" data-testid="contact-phone">{config.phone}</a>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">Е-пошта</h3>
                  <a href={`mailto:${config.email}`} className="text-orange-500 text-lg hover:underline" data-testid="contact-email">{config.email}</a>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">Адреса</h3>
                  <p className="text-gray-600 dark:text-gray-400" data-testid="contact-address">{config.address}</p>
                </div>
              </div>
              
              <div className="flex gap-4 pt-4">
                <a 
                  href={config.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-orange-500 hover:bg-orange-600 rounded-full flex items-center justify-center transition-colors"
                  data-testid="facebook-link"
                >
                  <Facebook className="h-6 w-6 text-white" />
                </a>
                <a 
                  href={config.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-orange-500 hover:bg-orange-600 rounded-full flex items-center justify-center transition-colors"
                  data-testid="instagram-link"
                >
                  <Instagram className="h-6 w-6 text-white" />
                </a>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-cream dark:from-gray-700 dark:to-gray-800 rounded-3xl shadow-xl p-8">
              <form action={config.formspreeEndpoint} method="POST" className="space-y-6" data-testid="contact-form">
                <input type="hidden" name="_replyto" value={config.contactFormRecipient} />
                <input type="hidden" name="_subject" value={`Нова порака од веб-страницата - ${config.cabinetName}`} />
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">Име и презиме</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors"
                    data-testid="form-name-input"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">Е-пошта</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors"
                    data-testid="form-email-input"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">Телефон</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors"
                    data-testid="form-phone-input"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">Порака</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors resize-none"
                    data-testid="form-message-input"
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  className="w-full py-4 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all"
                  data-testid="form-submit-button"
                >
                  Испрати порака
                </button>
              </form>
            </div>
          </div>
          
          <div className="mt-12 rounded-3xl overflow-hidden shadow-xl h-96">
            <iframe
              src={config.googleMaps.embedUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Google Maps Location"
              data-testid="google-maps-embed"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6">
            <img 
              src={config.logo}
              alt="Логосвет" 
              className="h-20 w-auto mx-auto mb-4"
            />
            <h3 className="text-2xl font-nunito font-bold mb-2">{config.cabinetName}</h3>
            <p className="text-gray-400">{config.cabinetNameSubtitle}</p>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <p className="text-gray-400">© 2024 {config.cabinetName}. Сите права задржани.</p>
          </div>
        </div>
      </footer>

      {/* Floating Call Button */}
      <a
        href={`tel:${config.phone}`}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform animate-bounce-slow"
        data-testid="floating-call-button"
      >
        <Phone className="h-7 w-7" />
      </a>

      {/* Scroll to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-24 right-6 z-50 w-12 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform opacity-0 hover:opacity-100 focus:opacity-100"
        data-testid="scroll-to-top-button"
      >
        <ChevronDown className="h-6 w-6 rotate-180" />
      </button>
    </div>
  );
}

export default App;
